import { Flex } from "antd";
import dayjs from "dayjs";
import { FC, useCallback, useId, useMemo, useState } from "react";
import {
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
} from "tm-shared/api";
import { displayDateFormat } from "tm-shared/constants";

import { ProgressLoader } from "@/app/ProgressLoader";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";
import { setShouldUpdateKpi } from "@/store/slices/offline";
import {
    getKpiGetProgressSvThunk,
    getKpiGetSalaryToPaySvThunk,
} from "@/store/slices/sv";
import { getKpiTargetDate } from "@/utils/business/getKpiTargetDate";

import { DashboardErrorBlock } from "../../components/DashboardErrorBlock";
import { DashboardInfoBlock } from "../../components/DashboardInfoBlock";
import { DashboardInfoModal } from "./components/DashboardInfoModal";
import { KPICollapse } from "./components/KPICollapse";
import { MMLCollapse } from "./components/MMLCollapse";
import { StyledSelfPlanWrapper } from "./Styled";

export type ModalInfoType =
    | {
          title: string;
          type: "fin_groups";
          details: { finGroups: string[] };
      }
    | {
          title: string;
          type: "progressive_scale";
          details: {
              currentPercent: number;
              progressive: { from: number; coefficient: number }[];
          };
      }
    | {
          title: string;
          type: "sequential_scale";
          details: { minComplete: number; maxComplete: number };
      };

interface SelfPlanProps {
    targetDB: "db-sv" | "db-ta";
}

export const SelfPlan: FC<SelfPlanProps> = ({ targetDB }) => {
    const dispatch = useAppDispatch();

    const { id: userId } = useAppSelector((state) => state.user);
    const { offlineMode, shouldUpdateKpi } = useAppSelector(
        (state) => state.offline,
    );
    const workState = useAppSelector((state) => state.work);
    const svState = useAppSelector((state) => state.sv);

    const { queue, progress } = useAsyncTaskQueue();

    const { motivation, motivationError, mmlStatisticsError } =
        targetDB === "db-sv" ? svState : workState;

    const dashboardInfoModalKey = useId();
    const [modalInfo, setModalInfo] = useState<ModalInfoType | null>(null);

    const motivationGroups = useMemo(
        () =>
            motivation?.motivationGroups
                ? Object.values(motivation.motivationGroups).sort(
                      (a, b) => a.group.order - b.group.order,
                  )
                : [],
        [motivation?.motivationGroups],
    );

    const isModalOpened = Boolean(modalInfo);

    const handleOpenModal = useCallback((info: ModalInfoType) => {
        setModalInfo(info);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalInfo(null);
    }, []);

    const hasAllErrors = motivationError && mmlStatisticsError;

    if (!motivation && !mmlStatisticsError && !motivationError) return null;

    const handleUpdate = async () => {
        const commonKpiParams = {
            employeeId: userId,
            targetDate: getKpiTargetDate(),
        };

        queue.add("Завантаження зарплати", () =>
            dispatch(
                targetDB === "db-sv"
                    ? getKpiGetSalaryToPaySvThunk(commonKpiParams)
                    : getKpiGetSalaryToPayThunk(commonKpiParams),
            ).unwrap(),
        );

        queue.add("Завантаження прогресу KPI", () =>
            dispatch(
                targetDB === "db-sv"
                    ? getKpiGetProgressSvThunk(commonKpiParams)
                    : getKpiGetProgressThunk(commonKpiParams),
            ).unwrap(),
        );

        try {
            await queue.runParallel();
        } catch (error) {
            console.error("Init error:", error);
        } finally {
            setTimeout(() => {
                queue.clear();

                dispatch(setShouldUpdateKpi(false));
            }, 2000);
        }
    };

    const isLoaderOpen = progress.isRunning || progress.tasks.length > 0;

    const makeDateHint = () => {
        const titles = [];

        if (motivation?.calculatedAt) {
            titles.push(
                `Оновлено: ${dayjs(motivation.calculatedAt).format(
                    displayDateFormat.dateTimeSeconds,
                )}`,
            );
        }

        if (motivation?.targetDate) {
            titles.push(
                `Продажі до: ${dayjs(motivation.targetDate).format(
                    displayDateFormat.date,
                )}`,
            );
        }

        return titles.length > 0
            ? titles.join("<br />")
            : "Ваші дані ще не прораховано";
    };
    return (
        <>
            <Flex vertical style={{ width: "100%" }}>
                {hasAllErrors ? (
                    <Flex vertical>
                        {motivationError && (
                            <DashboardErrorBlock title={motivationError} />
                        )}
                        {mmlStatisticsError && (
                            <DashboardErrorBlock title={mmlStatisticsError} />
                        )}
                    </Flex>
                ) : (
                    <StyledSelfPlanWrapper>
                        <DashboardInfoModal
                            modalKey={dashboardInfoModalKey}
                            isOpen={isModalOpened}
                            onClose={handleCloseModal}
                            groupName={modalInfo?.title}
                            modalType={modalInfo?.type}
                            details={modalInfo?.details}
                        />

                        {motivationError ? (
                            <DashboardErrorBlock title={motivationError} />
                        ) : (
                            <>
                                <MMLCollapse targetDB={targetDB} />
                                <DashboardInfoBlock
                                    title={makeDateHint()}
                                    onClickReload={
                                        shouldUpdateKpi &&
                                        !offlineMode &&
                                        handleUpdate
                                    }
                                />

                                {motivationGroups.map((group) => (
                                    <KPICollapse
                                        key={group.group.groupId}
                                        evaluatedMotivationGroup={group}
                                        setModalData={handleOpenModal}
                                    />
                                ))}
                            </>
                        )}
                    </StyledSelfPlanWrapper>
                )}
            </Flex>

            <ProgressLoader open={isLoaderOpen} tasks={progress.tasks} />
        </>
    );
};
